// Nagłówek i stopka strony wyników: data i stan wydarzenia, adresy logo, bety i partnerów.
// Czysta logika (bez Vue). Daty z API to czas polski (także z „Z”) — tylko przez parseApiTime,
// formatowanie zawsze w strefie Europe/Warsaw, niezależnie od strefy przeglądarki.
import { parseApiTime } from './polish-time.js'
import { ASSETS_BASE_URL, BETA_URL, SPONSOR_LOGOS_BASE_URL } from '../config.js'

const TIME_ZONE = 'Europe/Warsaw'
export const MAX_PARTNERS = 6

const fullDay = new Intl.DateTimeFormat('pl-PL', { timeZone: TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const weekdayLong = new Intl.DateTimeFormat('pl-PL', { timeZone: TIME_ZONE, weekday: 'long' })
const weekdayShort = new Intl.DateTimeFormat('pl-PL', { timeZone: TIME_ZONE, weekday: 'short' })
const dayMonth = new Intl.DateTimeFormat('pl-PL', { timeZone: TIME_ZONE, day: 'numeric', month: 'long' })
const dayParts = new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
const hourMinute = new Intl.DateTimeFormat('pl-PL', { timeZone: TIME_ZONE, hour: 'numeric', minute: '2-digit' })

function part(fmt, ms, type) {
  return fmt.formatToParts(new Date(ms)).find(p => p.type === type)?.value || ''
}

// Klucz dnia w Warszawie, np. "2026-10-25"
export function warsawDayKey(ms) {
  return dayParts.format(new Date(ms))
}

export function formatHourMinute(ms) {
  return ms == null ? '' : hourMinute.format(new Date(ms))
}

// "niedziela, 25 października 2026 · start 9:00"
// "sobota–niedziela, 24–25 października 2026 · start sob. 9:00"
// "piątek–niedziela, 30 października – 1 listopada 2026 · start pt. 18:00"
export function eventDateText(event) {
  const startMs = parseApiTime(event?.date_start)
  if (startMs == null) return ''
  const endMs = parseApiTime(event?.date_end)
  const start = formatHourMinute(startMs)
  if (endMs == null || endMs <= startMs || warsawDayKey(endMs) === warsawDayKey(startMs)) {
    return `${fullDay.format(new Date(startMs))} · start ${start}`
  }
  const days = `${weekdayLong.format(new Date(startMs))}–${weekdayLong.format(new Date(endMs))}`
  const sameYear = part(dayParts, startMs, 'year') === part(dayParts, endMs, 'year')
  const sameMonth = sameYear && part(dayParts, startMs, 'month') === part(dayParts, endMs, 'month')
  const year = part(dayParts, endMs, 'year')
  let range
  if (sameMonth) {
    range = `${part(dayMonth, startMs, 'day')}–${dayMonth.format(new Date(endMs))} ${year}`
  } else if (sameYear) {
    range = `${dayMonth.format(new Date(startMs))} – ${dayMonth.format(new Date(endMs))} ${year}`
  } else {
    range = `${dayMonth.format(new Date(startMs))} ${part(dayParts, startMs, 'year')} – ${dayMonth.format(new Date(endMs))} ${year}`
  }
  return `${days}, ${range} · start ${weekdayShort.format(new Date(startMs))} ${start}`
}

// 'before' | 'live' | 'ended' | null (brak daty startu). Bez daty końca: po starcie = na żywo.
export function eventPhase(event, nowMs) {
  const startMs = parseApiTime(event?.date_start)
  if (startMs == null) return null
  if (nowMs < startMs) return 'before'
  const endMs = parseApiTime(event?.date_end)
  if (endMs != null && nowMs > endMs) return 'ended'
  return 'live'
}

// "za 2 h 15 min", "za 12 min"; powyżej doby — pusty (wtedy sama plakietka „Przed startem”)
export function startsInText(event, nowMs) {
  const startMs = parseApiTime(event?.date_start)
  if (startMs == null || startMs <= nowMs) return ''
  const minutes = Math.ceil((startMs - nowMs) / 60000)
  if (minutes >= 24 * 60) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h ? `za ${h} h ${m} min` : `za ${m} min`
}

function joinUrl(base, file) {
  return `${String(base).replace(/\/+$/, '')}/${String(file).replace(/^\/+/, '').split('/').map(encodeURIComponent).join('/')}`
}

// Logo organizatora (events.logo_file) — te same pliki co w /ar i /loops
export function eventLogoUrl(event) {
  const file = typeof event?.logo_file === 'string' ? event.logo_file.trim() : ''
  if (!file) return ''
  return /^https?:\/\//i.test(file) ? file : joinUrl(`${ASSETS_BASE_URL}/files/logo/`, file)
}

export function betaEventUrl(alias) {
  return alias ? `${BETA_URL.replace(/\/+$/, '')}/events/${encodeURIComponent(alias)}` : ''
}

// Tylko http(s) — link javascript: w <a href> uruchomiłby się po kliknięciu (ta sama reguła co API i beta).
export function safePartnerLink(url) {
  const value = typeof url === 'string' ? url.trim() : ''
  return /^https?:\/\/\S+$/i.test(value) ? value : null
}

// sponsors z /events/alias → [{ key, src, href, name }] wg `sort`, max 6; wpisy bez pliku pomijane
export function normalizePartners(list, base = SPONSOR_LOGOS_BASE_URL) {
  if (!Array.isArray(list)) return []
  return list
    .filter(s => s && typeof s.file === 'string' && s.file.trim())
    .map((s, index) => ({ s, index }))
    .sort((a, b) => (Number(a.s.sort) || 0) - (Number(b.s.sort) || 0) || a.index - b.index)
    .slice(0, MAX_PARTNERS)
    .map(({ s, index }) => ({
      key: String(s.id ?? `${index}-${s.file}`),
      src: /^https?:\/\//i.test(s.file.trim()) ? s.file.trim() : joinUrl(base, s.file.trim()),
      href: safePartnerLink(s.url),
      name: typeof s.name === 'string' ? s.name.trim() : ''
    }))
}

// Krótkie nazwy tras tych samych zawodów: część nazwy po wspólnym początku ("Bieg Górski 2026 – 25 km" → "25 km").
export function routeLabels(events) {
  const names = events.map(e => String(e.name || e.alias || '').trim())
  if (names.length < 2) return names
  const words = names.map(n => n.split(/\s+/))
  let common = 0
  while (words.every(w => w.length > common + 1 && w[common] === words[0][common])) common++
  return words.map((w, i) => {
    const rest = w.slice(common).join(' ').replace(/^[-–—:·|,]+\s*/, '')
    return rest || names[i]
  })
}
